/**
 * schema-fetch — retrieves the canonical ProductSchema JSON from the Polyform
 * backend, keyed by SKU, and normalises it for the selection engine.
 *
 * The endpoint template contains a `{sku}` placeholder that this module fills.
 * Contract: Polyform API ProductSchema.
 */

/**
 * Build the concrete schema URL from an endpoint template and a SKU.
 * @param {string} endpointTemplate  e.g. "https://apip.polyformus.com/api/v1/shopify/sellables/{sku}/schema"
 * @param {string} sku
 * @returns {string|null}
 */
export function buildSchemaUrl(endpointTemplate, sku) {
  if (!endpointTemplate || !sku) return null;
  return endpointTemplate.replace('{sku}', encodeURIComponent(sku));
}

/**
 * Parse a Shopify GID string into its trailing numeric id.
 * "gid://shopify/ProductVariant/111" → "111". Returns null when absent.
 * @param {string|null|undefined} gid
 * @returns {string|null}
 */
export function gidToNumericId(gid) {
  if (!gid) return null;
  const tail = String(gid).split('/').pop();
  return tail || null;
}

/**
 * Normalise the raw API object into the shape the engine expects.
 * - Guarantees `options` and `variants` arrays exist.
 * - Derives a numeric `id` on each variant from `shopifyVariantId` (GID).
 *   Variants with no Shopify variant (id === null) are unpurchasable.
 *
 * @param {object} raw
 * @returns {object|null} normalised schema, or null if the required shape is missing
 */
export function normalizeSchema(raw) {
  if (!raw || !Array.isArray(raw.options) || !Array.isArray(raw.variants)) {
    return null;
  }

  const variants = raw.variants.map((v) => ({
    ...v,
    // Numeric Shopify id for cart / section-render APIs (null → unpurchasable).
    id: gidToNumericId(v.shopifyVariantId),
    optionValues: v.optionValues ?? {},
  }));

  return { ...raw, options: raw.options, variants };
}

/**
 * Fetch and normalise the schema for a SKU.
 * Throws on network / HTTP / parse failure so callers can degrade gracefully.
 *
 * @param {string} url  concrete schema URL (from buildSchemaUrl)
 * @param {AbortSignal} [signal]
 * @returns {Promise<object>} normalised schema
 */
export async function fetchSchema(url, signal) {
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    mode: 'cors',
    credentials: 'omit',
    // Revalidate rather than blindly reuse a cached schema, so content edits
    // aren't served stale while still allowing the API's Cache-Control/304s.
    cache: 'no-cache',
    signal,
  });
  if (!res.ok) {
    throw new Error(`Schema fetch failed: ${res.status}`);
  }
  const raw = await res.json();
  const normalised = normalizeSchema(raw);
  if (!normalised) {
    throw new Error('Schema fetch returned an unexpected shape');
  }
  return normalised;
}
